"use client";

import * as React from "react";
import {
  BarChart3,
  Bell,
  Bus,
  Calendar,
  CalendarDays,
  HelpCircle,
  Home,
  Megaphone,
  Menu,
  ScanFace,
  Settings,
  Shield,
  UserCheck,
  UserCog,
  Users,
  Wallet,
  Wrench,
  X,
  LogOut,
} from "lucide-react";
import { API_ENDPOINTS } from "../constants/config";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { clearStoredAuth, getToken } from "../utils/authStorage";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { filterMenuByFeatures } from "../utils/featureMenuFilter";

const EXTRA_TABS = [
  { id: "dashboard", permissionId: "dashboard", label: "Dashboard", path: "/dashboard/dashboard", icon: Home },
  { id: "students", permissionId: "students", label: "Students", path: "/dashboard/students", icon: Users },
  { id: "teachers", permissionId: "teachers", label: "Teachers", path: "/dashboard/teachers", icon: Users },
  { id: "staff", permissionId: "staff", label: "Staff", path: "/dashboard/staff", icon: UserCog },
  { id: "teacher-attendance", permissionId: "teacher-attendance", label: "Teacher Attendance", path: "/dashboard/teacher-attendance", icon: Users },
  { id: "registration-faces", permissionId: "registration-faces", label: "Registration Faces", path: "/dashboard/registration-faces", icon: ScanFace },
  { id: "payroll", permissionId: "payroll", label: "Payroll", path: "/dashboard/payroll", icon: Wallet },
  { id: "class-transition", permissionId: "class-transition", label: "Class Transition", path: "/dashboard/class-transition", icon: Users },
  { id: "classroom-mapping", permissionId: "classroom-mapping", label: "ClassRoom Mapping", path: "/dashboard/classroom-mapping", icon: UserCog },
  { id: "fee-management", permissionId: "fee-management", label: "Fee Management", path: "/dashboard/fee-management", icon: BarChart3 },
  { id: "bus-routes", permissionId: "bus-routes", label: "Bus Routes", path: "/dashboard/bus-routes", icon: Bus },
  { id: "vendor", permissionId: "vendor", label: "Vendor", path: "/dashboard/vendor", icon: Wrench },
  { id: "assets", permissionId: "assets", label: "Assets", path: "/dashboard/assets", icon: Wrench },
  { id: "inventory", permissionId: "inventory", label: "Inventory", path: "/dashboard/inventory", icon: Wrench },
  { id: "announcements-admin", permissionId: "announcements", label: "Announcements (Admin)", path: "/dashboard/announcements-admin", icon: Bell },
  { id: "holiday", permissionId: "holiday", label: "Holiday", path: "/dashboard/holiday", icon: Calendar },
  { id: "alert", permissionId: "alert", label: "Alert Management", path: "/dashboard/alert", icon: Shield },
  { id: "settings", permissionId: "settings", label: "Settings", path: "/dashboard/settings", icon: Settings },
  { id: "faq", permissionId: "faq", label: "FAQ", path: "/dashboard/faq", icon: HelpCircle },
];

const hasSettingsPermission = (permissions) => {
  if (!Array.isArray(permissions)) return false;
  return permissions.some((p) => p === "settings" || (p && p.startsWith("settings-")));
};

export function TeacherSidebar({ schoolSettings }) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentTab = location.pathname.split("/")[2] || "leave";
  const [user, setUser] = React.useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const { features } = useFeatureFlag();

  React.useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoading(true);
        const token = getToken();
        if (!token) {
          window.location.href = "/login";
          return;
        }

        const response = await fetch(API_ENDPOINTS.usersProfile, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 401) {
          // Token expired or invalid, redirect to login
          clearStoredAuth();
          window.location.href = "/login";
          return;
        }

        if (!response.ok) {
          throw new Error(`Failed to fetch user profile: ${response.status}`);
        }

        const data = await response.json();

        if (data.status === "success" && data.data && data.data.user) {
          if (!data.data.user._id && !data.data.user.id) {
            throw new Error("User ID is missing in the profile data");
          }
          const userData = {
            ...data.data.user,
            id: data.data.user._id || data.data.user.id, // Ensure ID is available in standard format
          };

          setUser(userData);
          localStorage.setItem("user", JSON.stringify(userData));
        } else {
          throw new Error("Invalid response format");
        }
      } catch (err) {
        console.error("Error fetching user profile:", err);
        setError(err.message);

        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            if (!parsedUser._id && !parsedUser.id) {
              clearStoredAuth();
              window.location.href = "/login";
              return;
            }
            if (!parsedUser.id && parsedUser._id) {
              parsedUser.id = parsedUser._id;
            }

            setUser(parsedUser);
            localStorage.setItem("user", JSON.stringify(parsedUser));
          } catch (e) {
            console.error("Error parsing stored user data:", e);
            clearStoredAuth();
            window.location.href = "/login";
          }
        } else {
          window.location.href = "/login";
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  const menuItems = [
    {
      id: "leave",
      label: "Leave Applications",
      icon: <Calendar className="w-5 h-5" />,
      path: "/dashboard/leave",
    },
    {
      id: "student-leave",
      label: "Student Leave",
      icon: <CalendarDays className="w-5 h-5" />,
      path: "/dashboard/student-leave",
    },
    {
      id: "announcements",
      label: "Announcements",
      icon: <Megaphone className="w-5 h-5" />,
      path: "/dashboard/announcements",
    },
    {
      id: "my-attendance",
      label: "My Attendance",
      icon: <UserCheck className="w-5 h-5" />,
      path: "/dashboard/my-attendance",
    },
    {
      id: "class-students",
      label: "Class Students",
      icon: <Users className="w-5 h-5" />,
      path: "/dashboard/class-students",
    },
    {
      id: "bustracking-teacher",
      label: "Bus Tracking",
      icon: <Bus className="w-5 h-5" />,
      path: "/dashboard/bustracking-teacher",
    },
  ];

  // Apply feature flag filtering to default menu items
  const defaultFiltered = filterMenuByFeatures(menuItems, features, 'teacher');

  // Apply feature flag filtering to extra tabs, then filter by permissions
  const userPermissions = user?.permissions || [];
  const extraTabsWithIcons = EXTRA_TABS.map((item) => ({
    ...item,
    icon: React.createElement(item.icon, { className: "w-5 h-5" }),
  }));
  
  const extraFilteredByFeatures = filterMenuByFeatures(extraTabsWithIcons, features, 'teacher');
  
  const extraFiltered = extraFilteredByFeatures.filter((item) => {
    if (item.permissionId === "settings" && hasSettingsPermission(userPermissions)) return true;
    if (item.permissionId === "settings") return userPermissions.includes("settings");
    return userPermissions.includes(item.permissionId);
  });

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const getFullName = (user) => {
    if (!user) return "Teacher";

    const firstName = user.firstName || "";
    const middleName = user.middleName || "";
    const lastName = user.lastName || "";

    return (
      [firstName, middleName, lastName].filter(Boolean).join(" ") || "Teacher"
    );
  };

  const getInitials = (user) => {
    if (!user || !user.firstName) return "T";

    const firstInitial = user.firstName.charAt(0);
    const lastInitial = user.lastName ? user.lastName.charAt(0) : "";

    return (firstInitial + lastInitial).toUpperCase();
  };

  const getEmployeeCode = (user) => {
    if (!user) return "No ID";
    return user.employeeCode || "No ID";
  };

  const hasValidId = (user) => {
    return user && (user.id || user._id);
  };

  if (loading && !user) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <div className="flex items-center justify-center w-full">
          <div className="text-sm text-muted-foreground">Loading...</div>
        </div>
      </div>
    );
  }

  if (user && !hasValidId(user)) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <div className="flex items-center justify-center w-full">
          <div className="text-sm text-red-500">
            User ID not found. Please{" "}
            <button
              onClick={() => {
                clearStoredAuth();
                window.location.href = "/login";
              }}
              className="underline font-medium"
            >
              log in again
            </button>
            .
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-gray-50 relative">
      <Button
        variant="ghost"
        className="lg:hidden fixed top-4 left-4 z-40 p-2"
        onClick={toggleSidebar}
      >
        {isSidebarOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <Menu className="w-6 h-6" />
        )}
      </Button>

      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 lg:hidden z-30"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside
        className={`
          fixed
          top-20 left-0 bottom-0
          w-[280px] lg:w-72
          bg-white border-r border-gray-200
          transform transition-transform duration-300 ease-in-out
          ${
            isSidebarOpen
              ? "translate-x-0"
              : "-translate-x-full lg:translate-x-0"
          }
          z-40
        `}
      >
        <div className="flex flex-col h-full min-h-0">
          <div className="p-6 border-b border-gray-200 flex-shrink-0">
            <button
              onClick={() => {
                navigate("/dashboard/profile");
                if (window.innerWidth < 1024) {
                  setIsSidebarOpen(false);
                }
              }}
              className="flex items-center gap-3 w-full hover:opacity-80 transition-opacity cursor-pointer"
            >
              <Avatar className="h-10 w-10">
                <AvatarImage
                  src={user?.profilePicture || ""}
                  alt={getFullName(user)}
                />
                <AvatarFallback>{getInitials(user)}</AvatarFallback>
              </Avatar>
              <div className="text-left">
                <h1 className="text-lg font-bold text-gray-800">
                  {getFullName(user)}
                </h1>
                <p className="text-sm text-gray-500">{getEmployeeCode(user)}</p>
              </div>
            </button>
          </div>
          <nav className="flex-1 min-h-0 p-4 overflow-y-auto overflow-x-hidden">
            <div className="space-y-4">
              {/* My tabs — default teacher sections */}
              <div>
                <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
                  My tabs
                </p>
                <div className="space-y-1">
                  {defaultFiltered.map((item) => (
                    <Button
                      key={item.id}
                      variant={currentTab === item.id ? "secondary" : "ghost"}
                      className={`w-full justify-start text-sm mb-1 min-h-[44px] ${
                        currentTab === item.id
                          ? "bg-[#0263cc] text-white"
                          : "text-gray-600 hover:text-white hover:bg-[#0263cc]"
                      }`}
                      onClick={() => {
                        navigate(item.path);
                        if (window.innerWidth < 1024) setIsSidebarOpen(false);
                      }}
                    >
                      {item.icon}
                      <span className="ml-3 whitespace-nowrap">{item.label}</span>
                    </Button>
                  ))}
                </div>
              </div>
              {/* Assigned tabs — from permissions */}
              {extraFiltered.length > 0 && (
                <div>
                  <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 border-t border-gray-200 pt-3">
                    Assigned tabs
                  </p>
                  <div className="space-y-1">
                    {extraFiltered.map((item) => (
                      <Button
                        key={item.id}
                        variant={currentTab === item.id ? "secondary" : "ghost"}
                        className={`w-full justify-start text-sm mb-1 min-h-[44px] ${
                          currentTab === item.id
                            ? "bg-[#0263cc] text-white"
                            : "text-gray-600 hover:text-white hover:bg-[#0263cc]"
                        }`}
                        onClick={() => {
                          navigate(item.path);
                          if (window.innerWidth < 1024) setIsSidebarOpen(false);
                        }}
                      >
                        {item.icon}
                        <span className="ml-3 whitespace-nowrap">{item.label}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </nav>
          <div className="p-4 border-t border-gray-200 flex-shrink-0">
            <Button
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50 min-h-[44px]"
              onClick={() => {
                clearStoredAuth();
                window.location.href = "/login";
              }}
            >
              <LogOut className="w-5 h-5" />
              <span className="ml-3">Logout</span>
            </Button>
          </div>
        </div>
      </aside>
      <main className="flex-1 overflow-auto w-full lg:ml-72">
        <div className="p-4 sm:p-6 lg:p-8 pt-16 lg:pt-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
